/**
 * Object shape derivation.
 *
 * Turns a sample of real objects into the content-free `ObjectShape` the design
 * compiler reasons about.
 *
 * **This function is the privacy boundary made concrete.** Objects go in, with
 * all their values; statistics come out, with none. Nothing downstream of here
 * — not the compiler, not the pool, not a model — ever sees what a user wrote.
 * Every value is reduced to a count, a coverage ratio, or a cardinality.
 *
 * The binding manifest (property key → role) stays on the caller's side and is
 * never carried in the result, which is what keeps the eventual genome safe.
 */

import { ROLE_KIND, DERIVATIVE_OF, enumPairKey, isVisualKind, type Role } from '@morph/spec';
import type { ObjectShape } from './types.js';

/** The private, per-tenant map. Lives here only for the duration of the call. */
export type BindingManifest = ReadonlyMap<string, Role>;

export interface SampleObject {
  readonly properties: Readonly<Record<string, unknown>>;
  readonly relationCount?: number;
}

export interface DeriveOptions {
  /** Cap the sample. Coverage stabilises fast, and reading a million objects to
   *  decide a layout is absurd. */
  maxSample?: number;
  /** Above this, an enum is treated as free-form and not offered for grouping.
   *  Twelve groups is already a lot to look at; fifty is a mess. */
  enumCardinalityCap?: number;
}

function isPopulated(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
}

/**
 * Derive a shape from a sample.
 *
 * Note what is counted and what is not: for enum roles we count **distinct
 * values** to know whether grouping would read as groups or as noise — but the
 * values themselves are hashed into a set and discarded. The count survives;
 * the content does not.
 */
export function deriveShape(
  objects: readonly SampleObject[],
  manifest: BindingManifest,
  opts: DeriveOptions = {},
): ObjectShape {
  const cap = opts.maxSample ?? 500;
  const enumCap = opts.enumCardinalityCap ?? 200;
  const sample = objects.slice(0, cap);
  const n = sample.length;

  const populated = new Map<Role, number>();
  const distinct = new Map<Role, Set<string>>();
  const orderedDistinct = new Map<Role, Set<string>>();
  /** Distinct value PAIRS, per unordered role pair. See `enumPairCardinality`
   *  on `ObjectShape`: two enum roles can each be groupable and still never
   *  cross, and only a joint count can say so. */
  const pairs = new Map<string, Set<string>>();
  let relationTotal = 0;
  /** Objects with at least one reference. `relationTotal` sums edges, which is
   *  a different signal and the wrong numerator for a density — see the note
   *  on the return value. */
  let relationObjects = 0;

  /**
   * Objects carrying a derivative's SOURCE but not the derivative. Task `0131`.
   *
   * One counter per derivative role, and a containment is `count === 0`. The
   * pass is already walking every object; this is the one boolean per pair that
   * per-role coverage cannot reconstruct.
   */
  const missingDerivative = new Map<Role, number>();

  for (const obj of sample) {
    relationTotal += obj.relationCount ?? 0;
    if ((obj.relationCount ?? 0) > 0) relationObjects += 1;
    /** Which roles THIS object carries, for the containment pass below. */
    const held = new Set<Role>();
    /** This object's enum values, for the pair pass below. Collected here and
     *  crossed after the loop over its properties, because a pair needs both
     *  halves and the property loop sees them one at a time. */
    const mine: [Role, string][] = [];
    for (const [key, value] of Object.entries(obj.properties)) {
      const role = manifest.get(key);
      if (!role) continue;               // unmapped properties are invisible here
      if (!isPopulated(value)) continue;
      populated.set(role, (populated.get(role) ?? 0) + 1);
      held.add(role);

      if (ROLE_KIND[role] === 'enum') {
        let set = distinct.get(role);
        if (!set) { set = new Set(); distinct.set(role, set); }
        // Stop accumulating once it is clearly free-form. Keeps the set small
        // and means we never hold more of a user's values than we need.
        if (set.size <= enumCap) set.add(String(value));
        mine.push([role, String(value)]);
      }
      if (ROLE_KIND[role] === 'number' || ROLE_KIND[role] === 'score'
          || ROLE_KIND[role] === 'date') {
        let set = orderedDistinct.get(role);
        if (!set) { set = new Set(); orderedDistinct.set(role, set); }
        // Past the cap the role is plainly continuous; retaining more private
        // values cannot change the generator's decision.
        if (set.size < enumCap) set.add(String(value));
      }
    }
    // Every unordered pair this object contributes. Capped the same way the
    // marginal sets are — past the cap the joint distribution is free-form
    // too, and holding more of a user's values than the decision needs is the
    // thing this layer exists to avoid.
    for (let i = 0; i < mine.length; i += 1) {
      for (let j = i + 1; j < mine.length; j += 1) {
        const [ra, va] = mine[i]!;
        const [rb, vb] = mine[j]!;
        if (ra === rb) continue;          // two keys carrying one role
        const k = enumPairKey(ra, rb);
        let set = pairs.get(k);
        if (!set) { set = new Set(); pairs.set(k, set); }
        if (set.size <= enumCap) set.add(ra < rb ? `${va}\u0000${vb}` : `${vb}\u0000${va}`);
      }
    }
    // Counted per OBJECT, after its properties are known, because the question
    // is about one object holding two roles and the property loop sees them one
    // at a time — the same reason the enum pair pass sits here.
    for (const [derivative, source] of Object.entries(DERIVATIVE_OF)) {
      if (held.has(source as Role) && !held.has(derivative as Role)) {
        missingDerivative.set(derivative as Role,
          (missingDerivative.get(derivative as Role) ?? 0) + 1);
      }
    }
  }

  const roles = [...populated.entries()]
    .map(([role, count]) => ({
      role,
      kind: ROLE_KIND[role],
      coverage: n === 0 ? 0 : count / n,
    }))
    .sort((a, b) => b.coverage - a.coverage);

  const enumCardinality: Record<string, number> = {};
  for (const [role, set] of distinct) enumCardinality[role] = set.size;

  const orderedCardinality: Record<string, number> = {};
  for (const [role, set] of orderedDistinct) orderedCardinality[role] = set.size;

  const enumPairCardinality: Record<string, number> = {};
  for (const [k, set] of pairs) enumPairCardinality[k] = set.size;

  /**
   * A containment claim only where the SOURCE is present at all.
   *
   * A derivative role nothing sources would otherwise report `true` — zero
   * objects carry the source, so zero of them are missing the derivative — and
   * `true` here means *"binding the derivative alone loses no picture"*, which
   * is a claim about a population that does not exist. Vacuous truth is exactly
   * the shape a consumer would act on wrongly, so the key is simply absent and
   * the consumer reads that as "not known".
   */
  /**
   * And `true` only when the WHOLE population was looked at.
   *
   * @Codex, P2 on #184: *"When `objects.length` exceeds `maxSample`, this
   * boolean describes only the first 500 objects but is exported as an
   * unconditional containment fact … if those 500 objects all carry both
   * `media.hero` and `media.thumb` while object 501 carries only the source,
   * this returns `media.thumb: true`; `pickHero()` then binds only the
   * thumbnail and the card-collapse path drops the source, leaving the
   * unsampled object blank."*
   *
   * The asymmetry is the fix: `false` is an EXISTENTIAL claim and one
   * counter-example proves it, so a sample can report it soundly. `true` is
   * UNIVERSAL and no sample can ever prove it. So a sampled derivation reports
   * what it found missing and stays silent about what it did not.
   *
   * Absent rather than `false` for the unknown case, because the two mean
   * different things to a consumer and this record already draws that
   * distinction one paragraph up: `false` is *"binding the derivative alone
   * loses a picture"*, absent is *"not known"*. Collapsing them would make a
   * 501-object list indistinguishable from one that really is uneven.
   */
  const derivativeCovers: Record<string, boolean> = {};
  const wholePopulation = objects.length <= n;
  for (const [derivative, source] of Object.entries(DERIVATIVE_OF)) {
    if (!populated.get(source as Role)) continue;
    const covered = (missingDerivative.get(derivative as Role) ?? 0) === 0;
    if (!covered) { derivativeCovers[derivative] = false; continue; }
    if (wholePopulation) derivativeCovers[derivative] = true;
  }

  const cardinality: ObjectShape['cardinality'] =
    objects.length <= 1 ? 'single' : objects.length <= 40 ? 'small-set' : 'large-set';

  return {
    cardinality,
    // This path samples SILENTLY — `objects.slice(0, cap)` above, cap 500 — and
    // until this line it never said so. A 501-object list whose first 500 agree
    // derived coverage 1 with `sampled` absent, so `deriveTemplate` would seed
    // every new object with a default the 501st refutes. The store's derivation
    // has reported its own denominator all along; this one had the same trap
    // and none of the reporting. @Codex, on task 0092 piece 2.
    sampled: objects.length > n,
    // Task `0131`. Emitted even when empty, so a consumer reading a key that is
    // not there learns "the source is absent" rather than "this deriver does
    // not answer" — and a shape from somewhere else, with the field absent
    // entirely, still reads as not known.
    derivativeCovers,
    // `av` and `doc` count. Both shape-derivation paths compared kinds by
    // VALUE, and both were short: first of `av`, so a pool of nothing but video
    // derived `hasMedia: false` and got `mediaWeight: 'text'`, and then of
    // `doc`, so a pool of nothing but PDFs did the same — @Codex found each.
    //
    // Read from `VISUAL_KINDS` now rather than restated. The list existed the
    // whole time as `baseline.ts`'s private `isVisual`, correct and including
    // `doc`, under a docstring saying it was *"named once so the next kind
    // added cannot repeat it"*. A name that two other packages cannot import is
    // not named once.
    hasMedia: roles.some((r) => isVisualKind(r.kind) && r.coverage > 0),
    // OBJECTS carrying a reference, over objects. The store's own
    // `crossDocDensity` has always carried the argument for both halves of
    // this: *"objects rather than references … a card with three references
    // into one note is still one card with a relation to draw"*, and
    // *"deliberately NOT scaled by ten … a number a generator reads should mean
    // what it says."* Both sentences were written beside this field and applied
    // to the other one.
    //
    // Divided by ten, a pool where every object holds one reference scored 0.1
    // against a threshold of 0.2 and never surfaced a relation at all. Summing
    // edges, one object with five references in a pool of twenty scored 0.25
    // and surfaced for the whole pool. Neither is a share of objects.
    relationDensity: n === 0 ? 0 : Math.min(relationObjects / n, 1),
    ...(Object.keys(enumCardinality).length ? { enumCardinality } : {}),
    ...(Object.keys(orderedCardinality).length ? { orderedCardinality } : {}),
    ...(Object.keys(enumPairCardinality).length ? { enumPairCardinality } : {}),
    roles,
  };
}

/** Belt and braces, mirroring `assertNoLeak` in the spec package: prove a
 *  derived shape carries none of the values it was derived from. Cheap, and it
 *  catches the future bug where someone adds a field to ObjectShape and forgets
 *  that everything here must be a statistic. */
export function assertShapeCarriesNoValues(
  shape: ObjectShape,
  values: readonly string[],
): void {
  const text = JSON.stringify(shape);
  /**
   * A WHOLE token, not a substring — and this is the third time this repo has
   * paid for the difference.
   *
   * `text.includes(v)` threw twice on data that is otherwise entirely static,
   * both times on the film runtime `118`:
   *
   * ```
   * "declared":{"since":"2026-08-25T21:15:47.118Z"}   a seed on millisecond .118
   * "scopeKey":"118ykii"                              a random scope key
   * ```
   *
   * The comment below used to end *"so the context comes with it now — a
   * collision is obvious on sight"*, which improved the DIAGNOSIS and left the
   * flake exactly where it was: the second collision still turned a green suite
   * red on a clock, and cost the same minutes over again.
   *
   * Neither is a leak, and neither is a leak for the same structural reason:
   * `118` is not a value in either string, it is three characters inside a
   * longer opaque token. A statistic that genuinely carried the runtime would
   * carry it as a value — `118`, or `"118 min"`, or `"runtime:118"` — and every
   * one of those still matches here.
   *
   * The boundary is applied only on the side where the value's own edge is a
   * word character, so a sample like `$5` or `(2001)` is still found. This is
   * the same correction `pool.ts` and `pool-prior.ts` already carry, arrived at
   * there from the other direction — needle `a` inside `card` silently voided
   * every contribution — and stated the same way: **the needle names what to
   * look for; it does not get to decide on a fragment of something else.**
   */
  const WORD = /[0-9A-Za-z_]/;
  const escape = (v: string): string => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const whole = (v: string): RegExp => new RegExp(
    (WORD.test(v[0]!) ? '\\b' : '') + escape(v) + (WORD.test(v[v.length - 1]!) ? '\\b' : ''),
  );
  const found = values.filter((v) => v.length > 0 && whole(v).test(text));
  if (found.length) {
    // WHERE it matched, not only what. The search is a plain substring over the
    // whole serialised shape — deliberately, because its job is to catch a
    // field somebody adds to ObjectShape without noticing everything here must
    // be a statistic, and a structural matcher would miss exactly the case
    // nobody anticipated. The price of that breadth is that a SHORT sample can
    // collide with unrelated metadata.
    //
    // Not hypothetical. `store/test/shape.test.ts` searches for the film
    // runtime "118", and every role in a store-derived shape carries
    // `declared.since` — an ISO timestamp taken from the property key's
    // `createdAt`, which is wall-clock. A seed landing on millisecond .118 puts
    // "118" into the shape legitimately, and this threw on CI once, on a test
    // whose data is otherwise entirely static. Diagnosed by @Codex. It cost
    // hours, because the message named the value and never the place.
    //
    // So the context comes with it now. A collision is obvious on sight — the
    // match sits inside a timestamp — and so is a real leak.
    const where = found.map((v) => {
      const at = text.search(whole(v));
      return `${v} in ...${text.slice(Math.max(0, at - 40), at + v.length + 20)}...`;
    });
    throw new Error(
      `object shape leaked ${found.length} value(s) from the sample:\n  ${where.join('\n  ')}`,
    );
  }
}
